import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Search, LogOut, MessageSquare, Users, Loader2, Building2, Smartphone, ClipboardList, FileSpreadsheet } from 'lucide-react';
import { cn } from '../../lib/utils';
import { setAppBadge, clearAppBadge } from '../../lib/appBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LabDashboard = () => {
  const { user, logout } = useAuth();
  const { i18n } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchChannels();
    const i = setInterval(fetchChannels, 5000);
    const hb = setInterval(() => { axios.post(`${API}/heartbeat`).catch(() => {}); }, 10000);
    axios.post(`${API}/heartbeat`).catch(() => {});
    return () => { clearInterval(i); clearInterval(hb); clearAppBadge(); };
  }, []);

  const fetchChannels = async () => {
    try {
      const r = await axios.get(`${API}/lab/channels`);
      const list = r.data || [];
      setChannels(list);
      const total = list.reduce((s, c) => s + (c.unread_count || 0), 0);
      setAppBadge(total);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[LabDashboard]', e?.message);
    } finally { setLoading(false); }
  };

  const filtered = channels.filter(ch => ch.client_name.toLowerCase().includes(searchTerm.toLowerCase()) || ch.client_email.toLowerCase().includes(searchTerm.toLowerCase()));

  const downloadInstallGuide = async () => {
    try {
      const lang = (i18n.language || 'fr').slice(0, 2);
      const r = await axios.get(`${API}/guide/mobile-install.pdf`, {
        params: { lang },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'JPD-Link_installation_mobile.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[LabDashboard]', e?.message);
    }
  };

  const formatTime = (ts) => { if (!ts) return ''; const d = new Date(ts), n = new Date(); return d.toDateString() === n.toDateString() ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); };

  if (loading) return <div className="h-[100dvh] flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="h-[100dvh] flex flex-col bg-background" data-testid="lab-dashboard">
      <header className="h-14 border-b border-border bg-background/95 backdrop-blur flex items-center justify-between px-3 sm:px-6 shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <img src="https://customer-assets.emergentagent.com/job_dental-chat-5/artifacts/vbzuvp2d_1000265186.jpg" alt="JPD" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
          <div><h1 className="font-bold text-sm sm:text-base">Jeff P. Dental</h1><p className="text-xs text-muted-foreground truncate max-w-[180px] sm:max-w-none">{user?.name}</p></div>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/lab/orders">
            <Button variant="ghost" size="sm" className="gap-2" data-testid="lab-nav-orders-btn">
              <ClipboardList className="h-4 w-4" /><span className="hidden sm:inline">Dossiers</span>
            </Button>
          </Link>
          <Link to="/lab/invoices">
            <Button variant="ghost" size="sm" className="gap-2" data-testid="lab-nav-invoices-btn">
              <FileSpreadsheet className="h-4 w-4" /><span className="hidden sm:inline">Factures</span>
            </Button>
          </Link>
          <Button variant="ghost" size="icon" onClick={downloadInstallGuide} title="Guide d'installation mobile (PDF)" data-testid="download-install-guide">
            <Smartphone className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4" /><span className="hidden sm:inline ml-2">Déconnexion</span></Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-full flex flex-col">
          <div className="p-3 sm:p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-2"><Users className="h-4 w-4 text-muted-foreground" /><h2 className="font-semibold text-sm">Mes clients ({channels.length})</h2></div>
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-9" data-testid="search-clients-input" /></div>
          </div>

          <ScrollArea className="flex-1">
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground"><MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" /><p className="text-sm">{searchTerm ? 'Aucun résultat' : 'Aucun client assigné'}</p></div>
            ) : (
              <div className="divide-y divide-border">
                {filtered.map((ch) => (
                  <Link key={ch.channel_id} to={`/lab/chat/${ch.channel_id}`} className="block" data-testid={`channel-${ch.channel_id}`}>
                    <div className={cn("p-3 sm:p-4 hover:bg-muted/50 active:bg-muted transition-colors", ch.unread_count > 0 && "bg-blue-50/50")}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-primary" /></div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{ch.client_name}</p>
                            {ch.client_city && <p className="text-xs text-muted-foreground">{ch.client_city}</p>}
                            <p className="text-xs text-muted-foreground truncate mt-0.5">{ch.last_message || 'Pas encore de message'}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className="text-[10px] sm:text-xs text-muted-foreground">{formatTime(ch.last_message_time)}</span>
                          {ch.unread_count > 0 && <Badge className="bg-blue-600 text-white text-xs px-1.5 py-0 h-5 min-w-[20px] flex items-center justify-center">{ch.unread_count}</Badge>}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default LabDashboard;
